/**
 * MasterSelect — reusable dropdown backed by /api/masters/lookup/{entity_type}.
 *
 * Renders a Select of active master rows (label = data.name / data.code). When
 * `allowOther` is true, appends an "Other (specify) →" option that reveals a
 * free-text input the user can fill in. The final `value` bubbled up via
 * `onChange` is always a plain string (label from master OR the custom text).
 *
 * Usage:
 *   <MasterSelect
 *     entityType="phone_make"
 *     value={form.brand}
 *     onChange={(v) => setForm({...form, brand: v})}
 *     placeholder="Choose brand"
 *     testId="device-brand"
 *   />
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const OTHER_TOKEN = "__other__";

export default function MasterSelect({
  entityType,
  value,
  onChange,
  placeholder = "Select…",
  allowOther = true,
  disabled = false,
  testId,
  className = "",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks explicit "Other" pick — persists even while the user's custom value is empty
  const [otherPicked, setOtherPicked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/masters/lookup/${entityType}`)
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType]);

  const labels = useMemo(() => new Set(rows.map((r) => r.label)), [rows]);

  // "isCustom" = user has a saved value that's not in the master list.
  const isCustom = !!(value && !labels.has(value));
  const showOtherInput = isCustom || otherPicked;

  const selectValue = showOtherInput ? OTHER_TOKEN : (value || "");

  const handleSelectChange = useCallback((v) => {
    if (v === OTHER_TOKEN) {
      setOtherPicked(true);
      onChange("");
    } else {
      setOtherPicked(false);
      onChange(v);
    }
  }, [onChange]);

  return (
    <div className={className}>
      <Select value={selectValue} onValueChange={handleSelectChange} disabled={disabled || loading}>
        <SelectTrigger data-testid={testId ? `${testId}-select` : undefined} className="mt-1.5">
          <SelectValue placeholder={loading ? "Loading…" : placeholder} />
        </SelectTrigger>
        <SelectContent>
          {rows.map((r) => (
            <SelectItem key={r.id} value={r.label} data-testid={testId ? `${testId}-opt-${r.label}` : undefined}>
              {r.label}
            </SelectItem>
          ))}
          {allowOther && (
            <SelectItem key="__other__" value={OTHER_TOKEN} data-testid={testId ? `${testId}-opt-other` : undefined}>
              Other (specify) →
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {showOtherInput ? (
        <Input
          className="mt-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your own…"
          data-testid={testId ? `${testId}-other-input` : undefined}
          autoFocus={otherPicked && !value}
        />
      ) : null}
    </div>
  );
}
